import { useUIStore } from '../store/uiStore';

// "Undo toast instead of a confirm" (docs/plans/panel-system.md, rule 6): the
// action happens right away in the UI (onHide), a toast offers Undo, and the
// real write only goes out once the toast's window closes with no Undo
// press. First user: DefensePlanPartyEditForm's "Remove party".
//
// Deliberately module-level state, not a store — nothing besides this file
// ever needs to read a pending commit, and a plain Map avoids teaching
// zustand about setTimeout handles.

export interface DeferredCommitOptions {
  /** Identifies this commit — scheduling the same key again flushes
   *  whatever was already pending for it first, so two removals of the same
   *  thing in a row commit the first instead of silently dropping it. */
  key: string;
  /** The toast's message, e.g. "Removed Jason Johnson". */
  message: string;
  /** The real write. Runs after `delayMs` unless Undo cancels it first. A
   *  rejection restores via `onUndo` and shows an error toast — most call
   *  sites' own store actions already roll themselves back on failure, so
   *  this is a backstop, not the only recovery path. */
  commit: () => Promise<void>;
  /** Called synchronously when scheduling: hide the thing immediately. */
  onHide: () => void;
  /** Called on Undo, or after a failed commit: restore what onHide hid. */
  onUndo: () => void;
  /** Default 6s — long enough to read and react to the toast. */
  delayMs?: number;
}

interface PendingCommit {
  timer: ReturnType<typeof setTimeout>;
  commit: () => Promise<void>;
  onUndo: () => void;
}

const DEFAULT_DELAY_MS = 6000;
const COMMIT_FAILED_MESSAGE = "Couldn't remove. Try again.";

const pending = new Map<string, PendingCommit>();

async function runCommit(key: string): Promise<void> {
  const entry = pending.get(key);
  if (!entry) return;
  pending.delete(key);
  try {
    await entry.commit();
  } catch {
    entry.onUndo();
    useUIStore.getState().showToast({ message: COMMIT_FAILED_MESSAGE });
  }
}

/** Runs whatever's pending for `key` right now, skipping its delay — used
 *  when the same key is scheduled again before the first commit landed. */
function flushKey(key: string): void {
  const entry = pending.get(key);
  if (!entry) return;
  clearTimeout(entry.timer);
  void runCommit(key);
}

export function scheduleDeferredCommit({
  key,
  message,
  commit,
  onHide,
  onUndo,
  delayMs = DEFAULT_DELAY_MS,
}: DeferredCommitOptions): void {
  flushKey(key);
  onHide();

  const timer = setTimeout(() => void runCommit(key), delayMs);
  pending.set(key, { timer, commit, onUndo });

  useUIStore.getState().showToast({
    message,
    durationMs: delayMs,
    action: {
      label: 'Undo',
      onPress: () => {
        const entry = pending.get(key);
        if (!entry) return;
        clearTimeout(entry.timer);
        pending.delete(key);
        entry.onUndo();
      },
    },
  });
}

/** Runs every pending deferred commit right now, skipping its delay — the
 *  page is going away (pagehide) or the session is ending (sign-out), and a
 *  hidden-but-uncommitted row must not silently come back for the next
 *  visitor to that data. */
export function flushDeferredCommits(): Promise<void> {
  const runs: Promise<void>[] = [];
  for (const key of Array.from(pending.keys())) {
    const entry = pending.get(key);
    if (!entry) continue;
    clearTimeout(entry.timer);
    runs.push(runCommit(key));
  }
  // Sign-out awaits this so the writes land while their credentials are
  // still valid; pagehide can't wait, so it just starts them.
  return Promise.all(runs).then(() => undefined);
}

// Registered once per module load (this module is a singleton in the
// bundle), and only where `window` exists — every importer today is a
// .web.tsx file or authStore.ts, whose native build never sees this branch.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => void flushDeferredCommits());
}
